/**
 * Tiny inline bar trend for table cells — Dispatch Deck chart quartet
 * (epic #897). Replaces the Usage page's div-bar sparkline idiom
 * (consumed there in epic PR 6).
 *
 * Fixed-pixel svg (no viewBox stretching) so bars stay crisp at table
 * cell scale. Colors come from Deck `fill-*` utility classes only.
 *
 * Accessibility: the svg is exposed as ONE image (`role="img"` +
 * mandatory `ariaLabel` + `<title>`); the bars are decoration. The
 * surrounding table row must carry the real numbers as text — a
 * sparkline is never the sole carrier of a value (DESIGN.md §9).
 *
 * Edges: empty `values` render a blank strip of `height`; an all-zero
 * series renders floor-height stubs so "present but idle" stays
 * visibly different from "no data".
 */

const BAR_WIDTH = 4;
const BAR_GAP = 2;
const MIN_BAR_HEIGHT = 1.5;

interface Props {
  readonly values: readonly number[];
  /** Pixel height of the strip. */
  readonly height?: number;
  readonly barClassName?: string;
  readonly ariaLabel: string;
  readonly testId?: string;
}

export function Sparkline({
  values,
  height = 24,
  barClassName = 'fill-signal',
  ariaLabel,
  testId,
}: Props): JSX.Element {
  const max = Math.max(1, ...values);
  const width = values.length === 0 ? BAR_WIDTH : values.length * (BAR_WIDTH + BAR_GAP) - BAR_GAP;
  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block"
      data-testid={testId}
    >
      <title>{ariaLabel}</title>
      {values.map((value, index) => {
        const barHeight = Math.max(MIN_BAR_HEIGHT, (Math.max(0, value) / max) * height);
        return (
          <rect
            key={index}
            x={index * (BAR_WIDTH + BAR_GAP)}
            y={height - barHeight}
            width={BAR_WIDTH}
            height={barHeight}
            rx={1}
            className={barClassName}
          />
        );
      })}
    </svg>
  );
}
